import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import settings from '@/settings/public'

const fetchGeolocation = async () => {
  const res = await fetch(`${settings.api.host}/api/generic/geolocation`)
  if (!res.ok) {
    throw new Error('Network response was not ok')
  }
  const geolocation = await res.json()
  return geolocation?.data
}

// Pure, reusable helper function
export const getGeolocationStatus = (geolocation: any, props: any) => {
  const { allow = [], deny = [] } = props || {}

  // No rules for this item — geo cannot gate it, regardless of lookup outcome
  if (allow.length === 0 && deny.length === 0) {
    return { isAllowed: true, isRestricted: false }
  }

  // Unknown country (lookup failed or IP unresolvable, e.g. iCloud Private Relay):
  // fail open — a geo outage must not hide UI (FZSPF-3792)
  if (!geolocation?.country?.iso_code) {
    return { isAllowed: true, isRestricted: false }
  }

  const country = geolocation.country.iso_code

  const isRestricted = deny.includes(country)
  const isAllowedByList = allow.length > 0 ? allow.includes(country) : true
  const isAllowed = !isRestricted && isAllowedByList

  return { isAllowed, isRestricted }
}

export const useGeolocation = (props?: any) => {
  const { enabled = true, allow = [], deny = [] } = props || {}

  const { data: geolocation, isLoading, ...rest } = useQuery({
    queryKey: ['geolocation'],
    queryFn: fetchGeolocation,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled,
  })

  // Main status based on hook's props
  const { isAllowed, isRestricted } = useMemo(() => {
    if (isLoading) {
      return { isAllowed: false, isRestricted: false }
    }
    return getGeolocationStatus(geolocation, { allow, deny })
  }, [geolocation, allow, deny, isLoading])

  // Returned utility function for checking different rule sets
  const checkGeolocation = (checkProps: { allow: string[], deny: string[] }) => {
    const { isAllowed } = getGeolocationStatus(geolocation, checkProps)
    return isAllowed
  }

  return {
    geolocation,
    isAllowed,
    isRestricted,
    isLoading,
    checkGeolocation,
    country: geolocation?.country?.iso_code,
    ...rest,
  }
}